/**
 * Poolaroo accessibility patches.
 *
 * Container for small JS-level accessibility fixes that can't live in
 * Liquid markup or CSS — typically because the target element is rendered
 * after page load by vendor or third-party code (Shopify's AJAX cart,
 * Google's Store Quality widget, etc.).
 *
 * Each patch is self-contained, namespaced via IIFE-scoped consts, and
 * documented inline with the audit number it addresses.
 *
 * Loaded sitewide via layout/theme.liquid (script tag with `defer`).
 */

(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // Patch 1: Cart status announcements
  // -------------------------------------------------------------------------
  //
  // The Archetype theme's cart updates via AJAX innerHTML replacement, which
  // screen readers do not announce by default. We watch the cart for any
  // mutation and write a synthesized announcement into a visually-hidden
  // ARIA live region (`#CartA11yStatus`, added in snippets/cart-drawer.liquid).
  //
  // Addresses WCAG audit issues #29 (quantity change), #30 (item removed),
  // #33 (subtotal update).

  const STATUS_EL_ID = 'CartA11yStatus';
  const CART_ITEMS_SELECTOR = '[data-cart-subtotal]';
  const SUBTOTAL_SELECTOR = '[data-subtotal]';

  // Debounce so a quantity-up followed by a server round-trip subtotal change
  // doesn't fire two announcements in quick succession.
  let announceTimer = null;
  let lastItemCount = null;
  let lastSubtotal = null;

  function announce(message) {
    const el = document.getElementById(STATUS_EL_ID);
    if (!el) return;
    // Clear first so identical consecutive messages still re-announce.
    el.textContent = '';
    // Tiny defer so screen readers register the textContent change.
    requestAnimationFrame(() => {
      el.textContent = message;
    });
  }

  // On /cart both the drawer form and the page form are in the DOM, and the
  // drawer comes first. theme.js doesn't build the drawer on the cart template,
  // so its subtotal stays at the page-load value: read the page form there.
  function cartRoot() {
    return document.getElementById('CartPageForm') ||
      document.getElementById('CartDrawerForm') ||
      document;
  }

  function readCartState() {
    const root = cartRoot();
    const itemsEl = root.querySelector(CART_ITEMS_SELECTOR);
    const subtotalEl = root.querySelector(SUBTOTAL_SELECTOR);
    return {
      itemCount: itemsEl ? parseInt(itemsEl.getAttribute('data-count'), 10) : null,
      subtotalText: subtotalEl ? subtotalEl.textContent.trim() : null,
    };
  }

  function handleCartChange() {
    clearTimeout(announceTimer);
    announceTimer = setTimeout(() => {
      const state = readCartState();
      if (state.itemCount === null) return;

      // First read after page load — baseline only, don't announce.
      if (lastItemCount === null) {
        lastItemCount = state.itemCount;
        lastSubtotal = state.subtotalText;
        return;
      }

      // Nothing changed: say nothing. Opening the drawer or a re-render of the
      // same cart rebuilds the markup without changing what's in it.
      if (state.itemCount === lastItemCount && state.subtotalText === lastSubtotal) {
        return;
      }

      let msg = '';
      if (state.itemCount < lastItemCount) {
        msg = 'Item removed from cart. ';
      } else if (state.itemCount > lastItemCount) {
        msg = 'Item added to cart. ';
      } else {
        msg = 'Cart updated. ';
      }
      msg += state.itemCount + ' ' + (state.itemCount === 1 ? 'item' : 'items');
      if (state.subtotalText) {
        msg += ', subtotal ' + state.subtotalText;
      }
      msg += '.';

      announce(msg);
      lastItemCount = state.itemCount;
      lastSubtotal = state.subtotalText;
    }, 250);
  }

  function initCartA11y() {
    if (!document.getElementById(STATUS_EL_ID)) return;

    // Baseline read so the first real change has something to compare against.
    const state = readCartState();
    lastItemCount = state.itemCount;
    lastSubtotal = state.subtotalText;

    // The item-count marker only exists once theme.js has rebuilt the cart
    // markup. The /cart page renders without it, so its first rebuild (the
    // shopper's first change) would become the baseline and go unannounced.
    // Seed the count from /cart.js there; the server-rendered subtotal is
    // current. (Elsewhere theme.js builds the drawer at load, and that first
    // build is the right silent baseline, so no request is needed.)
    if (lastItemCount === null && document.getElementById('CartPageForm')) {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((cart) => {
          if (lastItemCount === null) {
            lastItemCount = cart.item_count;
            lastSubtotal = readCartState().subtotalText;
          }
        })
        .catch(() => {});
    }

    // Watch the cart container for any DOM mutation — the AJAX cart replaces
    // entire subtrees, so a coarse-grained observer catches every update kind.
    // We hang it on document.body because Archetype scrapes both /cart and the
    // drawer, and the actual replacement targets differ slightly between the
    // two surfaces.
    //
    // Mutations inside the live region itself are ignored: announce() writes to
    // #CartA11yStatus, which sits inside body, so reacting to those writes made
    // every announcement trigger the next one (until 2026-10-16 screen readers
    // heard "Cart updated. 0 items, subtotal $0.00." about four times a second
    // on every page).
    const statusEl = document.getElementById(STATUS_EL_ID);
    const observer = new MutationObserver((mutations) => {
      if (!mutations.some((m) => !statusEl.contains(m.target))) return;
      // Only react if cart-related markers exist in the DOM right now.
      if (document.querySelector(CART_ITEMS_SELECTOR)) {
        handleCartChange();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // -------------------------------------------------------------------------
  // Patch 2: Title + third-party disclaimer on Google Store Quality iframe
  // -------------------------------------------------------------------------
  //
  // Google injects a Store Quality widget into our DOM as an <iframe> hosted
  // at google.com/shopping/merchantverse/, but their injection script does not
  // set a `title` attribute. WCAG 4.1.2 requires iframes to have a programmatic
  // name (audit #5). The iframe element itself lives in our document — only
  // its contents are cross-origin — so we can setAttribute() on it from here.
  //
  // Beyond the bare WCAG-required title, the audit also recommended an
  // accessibility disclaimer for third-party iframe content, identifying what
  // aspect is beyond our control and giving fallback contact information for
  // users who run into trouble. We inject that as a visually-hidden <div> and
  // tie it to the iframe via aria-describedby — so a screen reader reading the
  // iframe announces both the title (function) AND the disclaimer (context +
  // contact path), while sighted users see no visual change.
  //
  // We re-apply on a few delayed timers because Google's script may inject the
  // iframe after our DOMContentLoaded fires, and a defensive re-apply guards
  // against any future case where their script resets attributes after creation.
  // The `if (!iframe.hasAttribute('title'))` guard makes the operation idempotent
  // and avoids overwriting if Google ever adds their own title in the future.
  // The disclaimer div is keyed by ID, so re-runs don't duplicate it.
  //
  // Does NOT address audits #1 (missing accessible name on the in-iframe button),
  // #7 (nested interactive controls inside the iframe), or #16 (false positive,
  // unrelated). Those concern Angular components inside Google's cross-origin
  // iframe and are unreachable from our document. #17 (reflow obstruction at
  // narrow viewports) is handled separately via the CSS media-query hide in
  // poolaroo-overrides.css.

  const MERCHANT_IFRAME_ID = 'merchantwidgetiframe';
  const MERCHANT_IFRAME_TITLE = 'Google Store Quality widget';
  const MERCHANT_DISCLAIMER_ID = 'poolaroo-merchant-iframe-disclaimer';
  const MERCHANT_DISCLAIMER_TEXT =
    "This Store Quality widget is provided and controlled by Google. " +
    "Its contents are loaded from google.com and are outside Poolaroo's control. " +
    "If you have trouble accessing or interacting with this widget, please contact " +
    "Poolaroo support at support@poolaroo.com for assistance.";

  function ensureMerchantDisclaimerEl() {
    let el = document.getElementById(MERCHANT_DISCLAIMER_ID);
    if (el) return el;
    el = document.createElement('div');
    el.id = MERCHANT_DISCLAIMER_ID;
    el.className = 'visually-hidden';
    el.textContent = MERCHANT_DISCLAIMER_TEXT;
    document.body.appendChild(el);
    return el;
  }

  function setMerchantIframeAccessibilityAttrs() {
    const iframe = document.getElementById(MERCHANT_IFRAME_ID);
    if (!iframe) return;
    if (!iframe.hasAttribute('title')) {
      iframe.setAttribute('title', MERCHANT_IFRAME_TITLE);
    }
    if (!iframe.hasAttribute('aria-describedby')) {
      ensureMerchantDisclaimerEl();
      iframe.setAttribute('aria-describedby', MERCHANT_DISCLAIMER_ID);
    }
  }

  function initMerchantIframeA11y() {
    // Run immediately, and again after delays in case Google's script
    // injects the iframe asynchronously after our script runs.
    setMerchantIframeAccessibilityAttrs();
    setTimeout(setMerchantIframeAccessibilityAttrs, 1000);
    setTimeout(setMerchantIframeAccessibilityAttrs, 3000);
  }

  // -------------------------------------------------------------------------
  // Patch 3: Form success messages that arrive with the page
  // -------------------------------------------------------------------------
  //
  // The contact page and the PDP Ask-a-Question tab render their success
  // message (role="status", audit #40) on the page the form posts back to. A
  // live region only announces changes, so a message already in the HTML at
  // load is never read out. Re-insert its text once the page has loaded so
  // screen readers announce it. Plain-text messages only (the theme's
  // post_success string); anything with child elements is left alone.

  // Runs after window load plus a pause: until load, Chrome reports the
  // document as busy to assistive tech, and a status change made then may not
  // be read. The box keeps its height while empty so nothing below it moves.
  function announceLoadedStatusMessages() {
    const messages = document.querySelectorAll('.note--success[role="status"]');
    messages.forEach((el) => {
      if (el.children.length) return;
      const text = el.textContent.trim();
      if (!text) return;
      // Exact (fractional) height: offsetHeight rounds, which would move the
      // content below by a fraction of a pixel. The theme is border-box.
      el.style.minHeight = el.getBoundingClientRect().height + 'px';
      el.textContent = '';
      setTimeout(() => {
        el.textContent = text;
        el.style.minHeight = '';
        if (el.getAttribute('style') === '') el.removeAttribute('style');
      }, 150);
    });
  }

  function initStatusMessageA11y() {
    if (!document.querySelector('.note--success[role="status"]')) return;
    const run = () => setTimeout(announceLoadedStatusMessages, 500);
    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run, { once: true });
    }
  }

  // -------------------------------------------------------------------------
  // Init
  // -------------------------------------------------------------------------

  function init() {
    initCartA11y();
    initMerchantIframeA11y();
    initStatusMessageA11y();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
